import { useState } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import useGetFeatureSettings from "./hooks/useGetFeatureSettings";
import useUpdateFeatureSetting from "./hooks/useUpdateFeatureSetting";
import ListLoader from "../../common-components/ListLoader";

const formatName = (name) =>
  name
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const ConfirmDialog = ({ setting, nextValue, onConfirm, onCancel }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
    <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
      <h3 className="text-lg font-semibold text-gray-800 mb-2">Confirm Change</h3>
      <p className="text-gray-600 mb-1">
        You are about to{" "}
        <span className={`font-semibold ${nextValue === "enabled" ? "text-green-600" : "text-gray-500"}`}>
          {nextValue === "enabled" ? "enable" : "disable"}
        </span>{" "}
        <span className="font-semibold text-gray-800">{formatName(setting.name)}</span>.
      </p>
      <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5">
        This affects all VC bookings across the system. Please confirm before proceeding.
      </p>
      <div className="flex gap-3 justify-end">
        <button
          onClick={onCancel}
          className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className={`px-4 py-2 rounded-lg text-white text-sm font-medium ${
            nextValue === "enabled"
              ? "bg-green-600 hover:bg-green-700"
              : "bg-gray-500 hover:bg-gray-600"
          }`}
        >
          {nextValue === "enabled" ? "Enable" : "Disable"}
        </button>
      </div>
    </div>
  </div>
);

const FeatureSettings = () => {
  const { settings, isLoading, fetchSettings } = useGetFeatureSettings();
  const { updateSetting, isUpdating } = useUpdateFeatureSetting({ fetchSettings });
  const [pendingToggle, setPendingToggle] = useState(null);

  const handleToggleClick = (setting) => {
    const nextValue = setting.value === "enabled" ? "disabled" : "enabled";
    setPendingToggle({ setting, nextValue });
  };

  const handleConfirm = async () => {
    if (!pendingToggle) return;
    const { setting, nextValue } = pendingToggle;
    setPendingToggle(null);
    await updateSetting(setting.name, nextValue);
  };

  const handleCancel = () => setPendingToggle(null);

  return (
    <div className="p-6">
      <ToastContainer position="top-right" autoClose={3000} />
      {pendingToggle && (
        <ConfirmDialog
          setting={pendingToggle.setting}
          nextValue={pendingToggle.nextValue}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}

      <div className="mb-6">
        <h1 className="text-xl font-semibold text-gray-800">Feature Settings</h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage system-wide feature toggles. Changes take effect immediately.
        </p>
      </div>

      {isLoading ? (
        <ListLoader />
      ) : settings.length === 0 ? (
        <div className="p-6 text-center text-gray-400">No feature settings found.</div>
      ) : (
        <div className="flex flex-col gap-4 max-w-2xl">
          {settings.map((setting) => {
            const isEnabled = setting.value === "enabled";
            return (
              <div
                key={setting.name}
                className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <p className="font-semibold text-gray-800 text-base">
                      {formatName(setting.name)}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">{setting.description}</p>
                    {setting.updated_at && (
                      <p className="text-xs text-gray-400 mt-2">
                        Last updated:{" "}
                        {moment(setting.updated_at).format("DD MMM YYYY, h:mm A")}
                      </p>
                    )}
                  </div>

                  <button
                    onClick={() => handleToggleClick(setting)}
                    disabled={isUpdating}
                    aria-label={`Toggle ${setting.name}`}
                    className={`relative inline-flex items-center h-7 w-14 flex-shrink-0 rounded-full transition-colors duration-200 focus:outline-none ${
                      isEnabled ? "bg-green-500" : "bg-gray-300"
                    } ${isUpdating ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                  >
                    <span
                      className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform duration-200 ${
                        isEnabled ? "translate-x-8" : "translate-x-1"
                      }`}
                    />
                    <span className="sr-only">{isEnabled ? "ON" : "OFF"}</span>
                  </button>
                </div>

                <div className="mt-3">
                  <span
                    className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${
                      isEnabled
                        ? "bg-green-100 text-green-700"
                        : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {isEnabled ? "Enabled" : "Disabled"}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FeatureSettings;
